<template>
  <div class="modal-overlay">
    <div class="modal-content terms-modal">
      <h2>Terms and Conditions</h2>
      
      <div class="terms-content">
        <div class="terms-text" v-html="formattedTerms"></div>
      </div>

      <div class="acceptance-section">
        <label class="checkbox-container">
          <input 
            type="checkbox" 
            v-model="accepted"
            :disabled="loading"
          >
          I have read and agree to the Terms and Conditions
        </label>

        <button 
          @click="acceptTerms" 
          :disabled="!accepted || loading"
          class="accept-btn"
        >
          Accept and Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { userService } from '../services/api';

const termsContent = `
<h1>Action Network Incorporated Website/App User Agreement</h1>

<h2>Introduction</h2>
<p>This Website/App User Agreement ("Agreement") governs your access to and use of the Action Network Incorporated ("we", "us", "our") website/mobile application (the "App") and any related services provided by Action Network Incorporated (collectively, the "Services"). By ticking the box below and pressing "Accept and Continue" or otherwise accessing or using the Services, you ("Member", "you", "your") agree to be bound by this Agreement. If you do not agree to these terms, do not otherwise access or use the Services and we will delete your Member Account.</p>

<h2>Use of the App</h2>
<h3>Eligibility:</h3>
<p>You must be a member of Action Network Incorporated to use the App.</p>

<h3>Permitted Use:</h3>
<p>The App is intended for your personal and non-commercial use. You may not use the App for any unlawful or prohibited purpose. You are responsible for all activity that occurs under your Member Account.</p>

<h3>Prohibited Use:</h3>
<p>You agree not to:</p>
<ul>
  <li>Use the App to transmit any viruses, worms, defects, Trojan horses, malicious code or other items of a destructive nature.</li>
  <li>Use the App to harass, abuse, or impersonate any other person or entity.</li>
  <li>Interfere with or disrupt the integrity or performance of the App or the servers and networks connected to the App.</li>
  <li>Attempt to gain unauthorised access to any portion of the App, other accounts, computer systems, or networks connected to the App.</li>
  <li>Collect or use any personal information of other Members without their consent.</li>
  <li>Use the App for any commercial purpose without prior written consent from Action Network Incorporated.</li>
</ul>

<h2>Member Accounts</h2>
<h3>Member Account Creation:</h3>
<p>We have created a member user account for you ("Member Account") using the data made available by you when joining Action Network Incorporated and updated by you from time to time during your membership of Action Network Incorporated ("Member Data"). To use certain features of the App, you are required to maintain this Member Account. You are responsible for maintaining the confidentiality of your account credentials and for any activity that occurs under your Member Account.</p>

<h3>Account Information:</h3>
<p>You agree to provide accurate and complete information concerning your Member Account and Member Data and to update such information as necessary to keep it current.</p>

<h3>Deletion of Member Accounts:</h3>
<p>We may delete your Member Account if:</p>
<ul>
  <li>you cease being a member of Action Network Incorporated.</li>
  <li>you disagree with these terms or otherwise fail to enter into this Agreement or any amendments to it.</li>
  <li>you ask us to.</li>
</ul>

<h3>Member Data:</h3>
<p>We will amend Member Data:</p>
<ul>
  <li>from time to time to reflect relevant changes as they come to our attention;</li>
  <li>if you notify us of any inaccuracy in your Member Data which you are unable to amend yourself.</li>
  <li>from time to time as a function of formulae and other automated processes deployed in the delivery of the Services.</li>
</ul>

<h2>Privacy</h2>
<h3>Data Collection:</h3>
<p>Action Network Incorporated may collect certain information about you and your use of the App, such as your device information, location data, and usage data as well as the information you provide directly in using the App.</p>

<h3>Use of Information:</h3>
<p>Action Network Incorporated may use your information and Member Data for purposes such as:</p>
<ul>
  <li>Providing and improving the Services.</li>
  <li>Personalising your experience with the App.</li>
  <li>Communicating with you about the App and Action Network Incorporated.</li>
  <li>As may be authorised, necessary or desirable in furtherance of the purpose of Action Network Incorporated.</li>
  <li>Complying with legal and regulatory requirements.</li>
</ul>

<h3>Data Security:</h3>
<p>Action Network Incorporated takes reasonable measures to protect your information from unauthorised access, use, or disclosure. However, you acknowledge that no method of transmission over the internet or method of electronic storage is completely secure.</p>

<h3>Data Sharing:</h3>
<p>Action Network Incorporated may share your information with third parties in certain circumstances, such as:</p>
<ul>
  <li>Service providers who assist Action Network Incorporated in providing the Services.</li>
  <li>As may be authorised, necessary or desirable in furtherance of the purpose of Action Network Incorporated.</li>
  <li>Law enforcement or other authorities as required by law.</li>
</ul>

<h3>Networking & Referral Purpose:</h3>
<p>You acknowledge that one of the purposes of the App is to facilitate the exchange of information concerning Members for the purposes of referral of business and expansion of Members' business and personal networks. You agree that Action Network Incorporated and its members may share information you volunteer and maintain as part of your Member Account with third parties for this purpose.</p>

<h3>Your Rights:</h3>
<p>You may have certain rights regarding your personal information, such as the right to access, correct, or delete your information. Please contact Action Network Incorporated for more information about your rights on hello@actionnetwork.com.au. At all times your personal information will be dealt with in accordance with the Australian Privacy Principles set out in the Privacy Act 1988 (Cth).</p>

<h2>Acknowledgements, Consents and Undertakings</h2>
<h3>Third Party Information:</h3>
<p>Where you provide information about third parties when using the App you acknowledge and undertake that you will use reasonable endeavours to ensure the accuracy of that information and may only provide it with that third party's informed consent to the provision of that information and its use within and for the purposes of providing Services using the App.</p>

<h3>Member Data:</h3>
<p>Where you provide information about yourself you acknowledge that such information may be used within the App to provide the Services and may be subject to qualitative and statistical analysis, whether or not anonymised, the results of which analysis may be used in the provision of the Services.</p>

<h3>Integrity of member Data:</h3>
<p>Where you provide information about yourself you warrant that such information is true and accurate. If at any time you find that your Member Data is not accurate you will take steps to correct it. If you are unable to correct your Member Data you must inform us of this and ask us to correct it.</p>

<h3>Consent to Contact:</h3>
<p>You consent to Action Network Incorporated contacting you as part of the functionality of the App and the Services provided via the App.</p>

<h3>Consent to sharing of Member Data:</h3>
<p>you consent to Action Network sharing Member Data within the App including to other Members generally and specifically as part of the functionality of the App and the Services provided via the App.</p>

<h2>Intellectual Property</h2>
<h3>Ownership:</h3>
<p>The App and all related content, including but not limited to text, graphics, logos, and software, are owned by Action Network Incorporated or its licensors and are protected by copyright, trademark, and other intellectual property laws.</p>

<h3>Use Restrictions:</h3>
<p>You may not use, reproduce, distribute, modify, transmit, or create derivative works of any content from the App without the prior written consent of Action Network Incorporated.</p>

<h2>Disclaimer of Warranties</h2>
<p>THE APP AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. ACTION NETWORK INCORPORATED DOES NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE APP OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES, MALICIOUS CODE OR OTHER HARMFUL COMPONENTS AND YOU EXPRESSLY AGREE TO HOLD ACTION NETWORK INCORPORATED HARMELSS IN RESPECT OF THE TRANSMISSIOIN OF SUCH VIRUSES, MALICIOUS CODE OR OTHER HARMFUL COMPONENTS (INCLUDING SUBSEQUENT TRANSMISSION BY YOU TO THIRD PARTIES) ARISING FROM USE OF THE APP.</p>

<h2>Limitation of Liability</h2>
<p>IN NO EVENT SHALL ACTION NETWORK INCORPORATED BE LIABLE FOR ANY DAMAGES WHATSOEVER, INCLUDING BUT NOT LIMITED TO DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES, ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OF THE APP OR THE SERVICES, WHETHER BASED ON CONTRACT, TORT, STRICT LIABILITY, OR OTHERWISE, EVEN IF ACTION NETWORK INCORPORATED HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND YOU EXPRESSLY DISCLAIM ANY RIGHTS TO ANY DAMAGES OF ANY KIND AS AGAINST ACTION NETWORK INCORPORATED ARISING FROM USE OF THE APP.</p>

<h2>Governing Law</h2>
<p>This Agreement shall be governed by and construed in accordance with the laws of the State of Queensland and you submit to the exclusive jurisdiction of the Courts of Queensland.</p>

<h2>Changes to this Agreement</h2>
<p>Action Network Incorporated may update this Agreement from time to time. You will be notified of any material changes to this Agreement. By continuing to use the App after the effective date of any such changes, you agree to be bound by the revised Agreement.</p>

<h2>Contact</h2>
<p>If you have any questions about this Agreement, please contact Action Network Incorporated at hello@actionnetwork.com.au.</p>

<h2>Severability</h2>
<p>If any provision of this Agreement is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall remain in full force and effect.</p>

<h2>Entire Agreement</h2>
<p>This Agreement constitutes the entire agreement between you and Action Network Incorporated with respect to the App and Services and supersedes all prior or contemporaneous communications, representations, and understandings, whether oral or written.</p>

<h2>Waiver</h2>
<p>No waiver of any provision of this Agreement shall be effective unless in writing and signed by the party against whom the waiver is sought to be enforced.</p>
`;

export default {
  name: 'TermsAndConditions',
  setup() {
    const accepted = ref(false);
    const loading = ref(false);

    const formattedTerms = termsContent;

    const acceptTerms = async () => {
      if (!accepted.value) return;
      
      loading.value = true;
      try {
        await userService.acceptTerms();
        window.location.reload(); // Refresh to show main app
      } catch (error) {
        console.error('Error accepting terms:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      accepted,
      loading,
      formattedTerms,
      acceptTerms
    };
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.terms-modal {
  max-width: 800px;
  max-height: 90vh;
  width: 90%;
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.terms-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 4px;
  margin: 20px 0;
}

.terms-text {
  text-align: left;
  line-height: 1.6;
}

.terms-text h1 {
  font-size: 24px;
  margin-bottom: 20px;
}

.terms-text h2 {
  font-size: 20px;
  margin-top: 24px;
  margin-bottom: 16px;
}

.terms-text h3 {
  font-size: 18px;
  margin-top: 20px;
  margin-bottom: 12px;
}

.terms-text p {
  margin-bottom: 16px;
}

.terms-text ul {
  margin-left: 20px;
  margin-bottom: 16px;
}

.terms-text li {
  margin-bottom: 8px;
}

.acceptance-section {
  padding: 20px 0;
  border-top: 1px solid #dee2e6;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  user-select: none;
}

.accept-btn {
  width: 100%;
  padding: 12px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
}

.accept-btn:hover:not(:disabled) {
  background: #3367d6;
}

.accept-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .terms-modal {
    width: 95%;
    max-height: 95vh;
  }
}
</style> 